// Model Tags. AF_MAPPING_WORDS.md section 5 (Robert, I10 2026-10-21). Pure: the map in, tags out.
//
// * A tag is given the moment an item is mapped. It is saved with the Asset ID it belongs to.
// * The record holds what the item is (its SKM Type) and its number, never the letters.
//   The letters come from the Model Tag Prefixes list on the Lists page, so a prefix can change
//   (MTR to MTRL) with no code change and nothing on the site breaks. The number never changes.
// * Each type counts up on its own per site: Bus 1, 2, 3 reads BUS-0001, BUS-0002, BUS-0003.
// * Used once, ever. A removed item's number retires; the next item takes the next number.
//   The same item mapped again (same Item Key) gets its own number back.
// * Two iPads offline can pick the same number. The cloud keeps the first; the second is
//   renumbered to the next free number (survey-map-page.ts) and Robert is told.
//
// What this pass tags is what the Map knows. Parts recorded in Collect (a main .MB, the fuses in
// a fused disconnect, panel circuits nobody mapped from) get their tags when the One-Line build adds them.

import { codeOf, isTransfer, type MapLink, type MapNode, type MapTree, type Registry } from "./survey-map";
import { isUtility } from "./survey";
import { PREFIX_LIST, prefixOk, SKM_TYPES, type SkmType } from "./model-tag-prefixes";

export { PREFIX_LIST, prefixOk, SKM_TYPES, type SkmType };
export type Prefixes = Record<SkmType, string>;

export const DEFAULT_PREFIXES: Prefixes = Object.fromEntries(SKM_TYPES.map((t) => [t.type, t.prefix])) as Prefixes;

const TYPE_ORDER = SKM_TYPES.map((t) => t.type) as readonly string[];
const isType = (v: string): v is SkmType => TYPE_ORDER.includes(v);

/**
 * The prefixes in use: today's defaults, with any the Lists page changed laid over them.
 * A switched-off, malformed or repeated value is ignored, so a bad edit can never give two types one prefix.
 */
export function prefixesFrom(values: readonly { listName: string; value: string; group?: string; isActive: boolean }[]): Prefixes {
  const out: Prefixes = { ...DEFAULT_PREFIXES };
  for (const v of values) {
    if (v.listName !== PREFIX_LIST || !v.isActive) continue;
    const type = (v.group ?? "").trim();
    const p = v.value.trim().toUpperCase();
    if (isType(type) && prefixOk(p)) out[type] = p;
  }
  const seen = new Map<string, SkmType>();
  for (const t of TYPE_ORDER as SkmType[]) {
    const other = seen.get(out[t]);
    if (other) out[t] = DEFAULT_PREFIXES[t];
    seen.set(out[t], t);
  }
  return out;
}

/** One item the map says needs a tag. Item Key is stable for the item's life. */
export interface WantedTag {
  key: string;
  type: SkmType;
  /** The Asset ID the tag belongs to, site number kept. A dot ID for a part inside an asset. Blank for a cable. */
  assetId: string;
}

/** A tag record as stored (modelTags/{id}). The id is the type without spaces and the number: Motor-0001. */
export interface TagRecord {
  type: SkmType;
  number: number;
  key: string;
  assetId: string;
}

/** Point words name a place on an asset, not a device. Anything else is a position or breaker: a PD. */
const POINT_WORDS = new Set(["OUT", "LUGS", "SEC", "MAIN", "BUS", "PRIMARY", "NORMAL", "EMERGENCY"]);

/** The tags one asset node takes, by its equipment class. Empty when the class has no SKM type yet. */
export function typesForAsset(reg: Registry, assetId: string, asSource: boolean): SkmType[] {
  const a = reg.byId.get(assetId);
  if (!a) return [];
  if (isUtility(a)) return ["Utility", "Transformer"];
  const code = codeOf(a);
  const kind = a.equipmentType.trim().toLowerCase();
  if (code === "GEN" || kind === "generator") return ["Generator"];
  if (isTransfer(reg, assetId)) return ["Transfer Switch"];
  if (code === "XFMR" || kind === "transformer") return ["Transformer"];
  if (code === "VFD") return ["Bus", "VFD"];
  if (code === "UPS") return ["UPS"];
  if (code === "CAP" || kind.startsWith("capacitor")) return ["Filter"];
  // Line reactors: SKM's type is not confirmed yet (AF_MAPPING_WORDS section 19). No tag until it is.
  if (code === "LR" || kind === "line reactor") return [];
  return asSource ? [] : ["Bus"];
}

/** The Item Key a tag is saved under: the type in lower case without spaces, then the ID ("protectivedevice:01-ER1-MCC-1A-A1"). */
export const keyFor = (type: SkmType, id: string) => `${type.toLowerCase().replace(/ /g, "")}:${id}`;

/** The Asset ID of a position: its own QR when it has one (C4 bucket), else the dot ID (panel circuit 13). */
export function positionId(reg: Registry, assetId: string, point: string): string {
  const child = `${assetId}-${point}`;
  return reg.byId.has(child) ? child : `${assetId}.${point}`;
}

/**
 * Every item on the map that needs a tag, in walk order from the utility down.
 * The cable into a node comes before the node, the way power reaches it. Drafts are skipped:
 * nothing about them is saved yet.
 */
export function wantedTags(reg: Registry, tree: MapTree, links: readonly MapLink[]): WantedTag[] {
  const out: WantedTag[] = [];
  const seen = new Set<string>();
  const want = (type: SkmType, id: string, assetId: string) => {
    const key = keyFor(type, id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ key, type, assetId });
  };
  const byId = new Map(links.map((l) => [l.id, l]));
  const cable = (linkId: string) => {
    const l = byId.get(linkId);
    if (l && l.status !== "Removed" && (l.goesTo === "Equipment" || l.goesTo === "End load")) want("Cable", linkId, "");
  };
  const visit = (key: string) => {
    const n = tree.nodes.get(key);
    if (!n || n.draft) return;
    if (n.linkId) cable(n.linkId);
    tagNode(reg, n, byId, want);
    for (const k of tree.kids.get(key) ?? []) visit(k);
  };
  for (const r of tree.roots) visit(r);
  // Second sources and separate feeds are cables too.
  for (const s of tree.sides) cable(s.linkId);
  return out;
}

function tagNode(reg: Registry, n: MapNode, byId: Map<string, MapLink>, want: (t: SkmType, id: string, assetId: string) => void): void {
  if (n.kind === "source" || n.kind === "asset") {
    for (const t of typesForAsset(reg, n.ref, n.kind === "source")) want(t, n.ref, n.ref);
  } else if (n.kind === "point") {
    if (!POINT_WORDS.has(n.ref.toUpperCase())) {
      const id = positionId(reg, n.asset, n.ref);
      want("Protective Device", id, id);
    }
  } else if (n.kind === "load") {
    const l = byId.get(n.ref);
    if (!l || l.status === "Removed") return;
    const from = POINT_WORDS.has(l.fromPoint.toUpperCase()) ? l.fromAsset : positionId(reg, l.fromAsset, l.fromPoint);
    want(l.endLoadType === "Non-motor" ? "Load" : "Motor", l.id, from);
  }
}

const pad = (n: number) => String(n).padStart(4, "0");

/** The record's ID: the type without spaces and four digits. It never holds the prefix. */
export const tagId = (t: Pick<TagRecord, "type" | "number">) => `${t.type.replace(/ /g, "")}-${pad(t.number)}`;

/** Reads a record ID back: "ProtectiveDevice-0012" to its type and number. Null when it is not one of ours. */
export function parseTagId(id: string): { type: SkmType; number: number } | null {
  const m = /^([A-Za-z]+)-(\d{4})$/.exec(id);
  if (!m) return null;
  const type = SKM_TYPES.find((t) => t.type.replace(/ /g, "") === m[1])?.type;
  const number = Number(m[2]);
  return type && number > 0 ? { type, number } : null;
}

/** The tag as people and SKM read it: today's prefix and the number. MTR-0004. */
export const tagName = (t: Pick<TagRecord, "type" | "number">, p: Prefixes = DEFAULT_PREFIXES) => `${p[t.type]}-${pad(t.number)}`;

/**
 * The tags still to give out. Items that already hold a tag keep it. Each new item takes the next
 * number after the highest ever used in its type, retired and refused numbers included.
 * `used` lists record IDs that may not be in `known` (a refused pick), so they are never reused.
 */
export function assignTags(wanted: readonly WantedTag[], known: readonly TagRecord[], used: readonly string[] = []): TagRecord[] {
  const has = new Set(known.map((k) => k.key));
  const top = new Map<string, number>();
  for (const id of [...known.map(tagId), ...used]) {
    const p = parseTagId(id);
    if (p) top.set(p.type, Math.max(top.get(p.type) ?? 0, p.number));
  }
  const fresh: TagRecord[] = [];
  for (const w of wanted) {
    if (has.has(w.key)) continue;
    const number = (top.get(w.type) ?? 0) + 1;
    if (number > 9999) continue;
    top.set(w.type, number);
    has.add(w.key);
    fresh.push({ type: w.type, number, key: w.key, assetId: w.assetId });
  }
  return fresh;
}

/** The stored record, named like the rest of the survey's records. No prefix: it is read from the list. */
export function tagRow(t: TagRecord, s: { siteNumber: string; badgeId: string; holderName: string; deviceName: string; at: string }): Record<string, unknown> {
  return {
    "SKM Type": t.type,
    Number: t.number,
    "Item Key": t.key,
    "Asset ID": t.assetId,
    "Site Number": s.siteNumber,
    "Recorded By": s.badgeId,
    "Holder Name": s.holderName,
    "Recorded On": s.deviceName,
    "Recorded At": s.at,
  };
}

export function tagFromRow(d: Record<string, unknown>): TagRecord | null {
  const type = String(d["SKM Type"] ?? "");
  const number = d.Number;
  const key = String(d["Item Key"] ?? "");
  if (!isType(type) || typeof number !== "number" || !Number.isInteger(number) || number < 1 || !key) return null;
  return { type, number, key, assetId: String(d["Asset ID"] ?? "") };
}

/**
 * Cloud tags, then this iPad's, by Item Key. If the cloud already holds the number this iPad picked for
 * another item, the cloud wins: that local pick comes back in `lost`, to be renumbered.
 */
export function mergeTags(cloud: readonly TagRecord[], local: readonly TagRecord[]): { tags: TagRecord[]; lost: TagRecord[] } {
  const byId = new Map(cloud.map((t) => [tagId(t), t]));
  const byKey = new Map(cloud.map((t) => [t.key, t]));
  const lost: TagRecord[] = [];
  for (const t of local) {
    const owner = byId.get(tagId(t));
    if (owner && owner.key !== t.key) {
      lost.push(t);
      continue;
    }
    if (!byKey.has(t.key)) {
      byKey.set(t.key, t);
      byId.set(tagId(t), t);
    }
  }
  return { tags: [...byKey.values()], lost };
}

/** SKM's name for the item: the tag, then the Asset ID without the site number. Section 4. */
export function skmName(t: TagRecord, p: Prefixes = DEFAULT_PREFIXES): string {
  const id = t.assetId.replace(/^\d{2}-/, "");
  return id ? `${tagName(t, p)} (${id})` : tagName(t, p);
}

/** The tags that belong to one asset or part, in type order. For the card header. Not the load it feeds. */
export function tagsForAsset(tags: readonly TagRecord[], assetId: string): TagRecord[] {
  return tags.filter((t) => t.assetId === assetId && t.type !== "Motor" && t.type !== "Load").sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type));
}

/** The motor or load a load node carries. */
export function tagsForLoad(tags: readonly TagRecord[], linkId: string): TagRecord[] {
  return tags.filter((t) => t.key === `motor:${linkId}` || t.key === `load:${linkId}`);
}
